<template>
    <v-dialog
        :id="modalId"
        lazy
        :max-width="$vuetify.breakpoint.smAndDown ? '95%' : '50%'"
        v-model="showModal"
    >
        <v-card v-if="showModal">
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                    color="secondary"
                    fab
                    small
                    @click.stop="showModal = false"
                >
                    <v-icon>close</v-icon>
                </v-btn>
            </v-card-actions>
            <v-card-text>
                <v-container fluid v-if="extractedShippingPrices.length">
                    <v-layout row wrap hidden-xs-only font-weight-bold>
                        <v-flex xs3>
                            {{ 'Shipping to' | translation }}
                        </v-flex>
                        <v-flex xs3>
                            {{ 'Shipping' | translation }}
                        </v-flex>
                        <v-flex xs3>
                            {{ 'free shipping from' | translation }}
                        </v-flex>
                        <v-flex xs3>
                            {{ 'delivery time' | translation }}
                        </v-flex>
                    </v-layout>
                    <v-layout
                        v-for="(shippingPrice, index) in extractedShippingPrices"
                        :key="index"
                        row wrap
                    >
                        <v-flex xs12 sm3>
                            <strong class="hidden-sm-and-up">{{ 'Shipping to' | translation }}</strong>
                            <template v-if="$country.getCountryNameBy('id', shippingPrice.country_id)">
                                {{ $country.getCountryNameBy('id', shippingPrice.country_id) | translation }}
                            </template>
                            <template v-else>{{ 'All countries' | translation }}</template>
                        </v-flex>
                        <v-flex xs12 sm3>
                            <strong class="hidden-sm-and-up">{{ 'Shipping' | translation }}</strong>
                            {{shippingPrice.value | currency}}
                        </v-flex>
                        <v-flex xs12 sm3>
                            <strong class="hidden-sm-and-up">{{ 'free shipping from' | translation }}</strong>
                            <template>
                                {{shippingPrice.freeValueMinimum | currency}}<br />
                                <span class="caption" v-if="getItemPriceLeftToFreeShipping(shippingPrice) > 0">
                                    {{ 'Further' | translation }}
                                    {{getItemPriceLeftToFreeShipping(shippingPrice) | currency}}
                                    {{ 'needed' | translation }}
                                </span>
                            </template>
                        </v-flex>
                        <v-flex xs12 sm3>
                            <strong class="hidden-sm-and-up">{{ 'delivery time' | translation }}</strong>
                            {{getShippingEstimationString(shippingPrice)}}
                        </v-flex>
                    </v-layout>
                    <v-layout row wrap v-if="isPickupable()" class="info--text font-weight-bold">
                        <v-flex xs12>
                            {{ 'This product can be picked up directly from the dealer.' | translation }}
                        </v-flex>
                    </v-layout>
                </v-container>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
import Vue from 'vue';
import { createNamespacedHelpers } from 'vuex';

const parentModuleName = 'Product';
const { mapGetters, mapState, mapActions } = createNamespacedHelpers(parentModuleName);

export default {
    name: 'ShippingModal',
    props: [
        'modalId',
        'merchant',
        'shippingType',
        'value',
    ],
    data() {
        return {
            shippingPrices: [],
            isActive: false,
        };
    },
    computed: {
        ...mapState({
            currentProductIdState: state => state.currentProductId,
            storeShippingPrices: state => state.shippingPrices,
            product: state => state.product,
        }),
        ...mapGetters([
            'getAmountValue',
        ]),
        itemPrice() {
            const basketTotal = this.$basket.getBasketTotal(true);
            const shippingTotal = this.$basket.getShippingTotal();
            return basketTotal - shippingTotal;
        },
        showModal: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        extractedShippingPrices() {
            let result = [];
            if (this.storeShippingPrices && this.merchant) {
                const shippingPrices = this.storeShippingPrices[this.merchant.data.id];
                const prices = [];
                const freePrices = {};
                if (shippingPrices) {
                    shippingPrices.forEach(shippingPrice => {
                        if (shippingPrice.value == 0) {
                            freePrices[shippingPrice.country_id] = shippingPrice;
                        } else {
                            prices.push(shippingPrice);
                        }
                    });
                }
                const extractedShippingPrices = prices.map(price => {
                    if (freePrices[price.country_id] && freePrices[price.country_id].minimum < this.itemPrice) {
                        const freePrice = freePrices[price.country_id];
                        freePrice.freeValueMinimum = freePrice.minimum;
                        Vue.delete(freePrices, price.country_id);
                        return freePrice;
                    } else if (freePrices[price.country_id]) {
                        const freePrice = freePrices[price.country_id];
                        price.freeValueMinimum = freePrice.minimum;
                        Vue.delete(freePrices, price.country_id);
                        return price;
                    } else {
                        return price;
                    }
                });
                if (Object.values(freePrices).length > 0) {
                    result = extractedShippingPrices.concat(Object.values(freePrices));
                } else {
                    result = extractedShippingPrices;
                }
            }

            return result;
        },
        currentProductId() {
            return this.currentProductIdState || this.product.id;
        },
    },
    methods: {
        ...mapActions(['getShippingPrices']),
        getItemPriceLeftToFreeShipping(shippingPrice) {
            return shippingPrice.freeValueMinimum - this.itemPrice;
        },
        beforeShow() {
            this.getShippingPrices({
                merchant: parseInt(this.merchant.data.id),
                type: parseInt(this.shippingType),
            });
        },
        getShippingEstimationString(shippingPrice) {
            let estimationString = '';
            let shippingOffset;

            if (this.merchant.productAttributes) {
                if (this.merchant.productAttributes[this.currentProductId]['mb.time_to_shipable']) {
                    shippingOffset = this.merchant.productAttributes[this.currentProductId]['mb.time_to_shipable'];
                } else {
                    shippingOffset = 0;
                }

                if (shippingPrice.hasOwnProperty('shipping_time_from')) {
                    estimationString += parseInt(shippingPrice.shipping_time_from) + parseInt(shippingOffset);
                }
                if (
                    shippingPrice.hasOwnProperty('shipping_time_from') &&
                    shippingPrice.hasOwnProperty('shipping_time_to') &&
                    shippingPrice.shipping_time_from !== shippingPrice.shipping_time_to
                ) {
                    estimationString += ' - ' + (parseInt(shippingPrice.shipping_time_to) + parseInt(shippingOffset));
                }
                if (shippingPrice.hasOwnProperty('shipping_time_from')) {
                    estimationString += ' Werktag(e)';
                }
            }

            return estimationString;
        },
        isPickupable() {
            let productPickupable = this.merchant.productAttributes[this.currentProductId]['system.pickupable'] == true;
            let merchantPickupable = this.merchant.data.attributes.allow_pickup == true;

            return productPickupable || merchantPickupable;
        },
    },
    watch: {
        showModal(value) {
            if (value) {
                this.beforeShow();
            }
        },
    },
};
</script>